import React from 'react';
import { MessageSquare, CheckCircle, CreditCard, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export default function ProcessSection() {
  const steps = [
    {
      icon: MessageSquare,
      title: 'Contact Us',
      description: 'Contact us with your business currency requirements',
      color: 'from-blue-600 to-blue-700'
    },
    {
      icon: CheckCircle,
      title: 'Confirm Rate',
      description: 'Confirm your corporate rate with our trading desk',
      color: 'from-green-600 to-green-700'
    },
    {
      icon: CreditCard,
      title: 'Settle Securely',
      description: 'Execute your business transaction securely',
      color: 'from-purple-600 to-purple-700'
    }
  ];

  return (
    <section id="process" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
            How It Works
          </h2>
          <div className="w-24 h-1 bg-green-600 mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Our streamlined corporate process ensures quick, secure, and professional currency exchange for your business operations and international transactions.
          </p>
        </div>

        <div className="relative">
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <Card className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-slate-50 to-white">
                  <CardContent className="p-8 pt-12 text-center relative">
                    {/* Number in top-left corner */}
                    <div className="absolute top-4 left-4 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className={`w-24 h-24 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <step.icon className="w-12 h-12 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>

                {/* Arrow between steps */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-green-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Process Timeline for Mobile */}
        <div className="md:hidden mt-12">
          <div className="flex flex-col space-y-4">
            {steps.map((_, index) => (
              index < steps.length - 1 && (
                <div key={index} className="flex justify-center">
                  <div className="w-px h-8 bg-green-600"></div>
                </div>
              )
            ))}
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 text-lg mb-6">
            The entire process typically takes less than 24 hours for most transactions.
          </p>
          <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-6 py-3 rounded-full border border-green-200">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Fast • Secure • Professional</span>
          </div>
        </div>
      </div>
    </section>
  );
}